import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Menu,
  MenuItem,
  InputAdornment,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { AddCircle, MoreHoriz, Visibility } from '@material-ui/icons';
import CreateIcon from '@material-ui/icons/Create';
import { useDispatch, useSelector } from 'react-redux';
import {
  GUEST_ROOM_EXPERIENCE_TEMPLATE_ADD_ROOM,
  GUEST_ROOM_EXPERIENCE_TEMPLATE_CREATE,
  GUEST_ROOM_EXPERIENCE_TEMPLATE_EDIT,
} from '../../../../../../../constants/GuestRoomExperienceConst';
import DeletePopupModel from '../../../../../../../shared/delete-popup/Delete-Popup';
import LinearProgressBar from '../../../../../../../shared/progress-bar/Progress';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { fetchGRETemplates, greTemplatesSelector } from '../../../../../configurationSlice';
import {
  deleteExisitingTemplate,
  duplicateExisitingTemplate,
} from '../../../../../../../utils/IntegrationsAPIUtils';
import { notificationManager } from '../../../../../../../utils/NotificationUtils';

const GuestRoomExperienceTemplateList = (props) => {
  const { onDrawerToggle, selectedHotel, onViewToggleTemplate } = props;
  const [selectedValue, setSelectedValue] = useState({
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    anchorEl: null,
  });
  const originalTemplates = useSelector(greTemplatesSelector);
  const dispatch = useDispatch();
  const [templates, setTemplates] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchButton, setSearchButton] = useState(true);

  useEffect(() => {
    // assigning data set to local list while keeping an orginal data set for filtering
    setTemplates(originalTemplates);
    setLoading(false);
    setSearchText('');
    setSearchButton(true);
  }, [originalTemplates]);

  useEffect(() => {
    if (originalTemplates) {
      const filteredRows = originalTemplates.filter((item) => {
        return item.name.toLowerCase().includes(searchText.toLowerCase());
      });
      setTemplates(filteredRows);
    }
  }, [originalTemplates, searchText]);

  const onOpenDrawer = (event, type) => {
    event.stopPropagation();
    onDrawerToggle(type, true, selectedValue.template);
    handleClose();
  };

  const handleClose = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      template: null,
    });
  };

  const handleCellMenuClick = (event, template) => {
    event.stopPropagation();
    setSelectedValue({
      id: template.id,
      name: template.name,
      title: '',
      description: '',
      status: false,
      anchorEl: event.currentTarget,
      template: template,
    });
  };

  const deleteTemplate = () => {
    handleClose();
    if (selectedValue && selectedValue?.id) {
      originalTemplates?.find((temp) => temp.id === selectedValue?.id)?.rangeList?.length === 0
        ? deleteExisitingTemplate(selectedHotel, selectedValue?.id)
            .then((res) => {
              dispatch(fetchGRETemplates(selectedHotel));
              notificationManager('success', 'Template deleted successfully');
            })
            .catch((err) => {
              notificationManager('danger', 'Unable to delete template');
            })
        : notificationManager('danger', 'Unable to delete template');
    }
  };

  const onDeleteCancel = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
    });
  };

  const deleteFlag = () => {
    setSelectedValue((prev) => ({
      ...prev,
      title: 'Are you sure you want to delete',
      description: 'This action cannot be reversed',
      status: true,
      anchorEl: null,
    }));
  };

  const duplicateTemplate = () => {
    handleClose();
    if (selectedValue && selectedValue?.id) {
      duplicateExisitingTemplate(selectedHotel, selectedValue?.id)
        .then((res) => {
          dispatch(fetchGRETemplates(selectedHotel));
          notificationManager('success', 'Template duplicated successfully');
        })
        .catch((err) => {
          notificationManager('danger', 'Unable to duplicate template');
        });
    }
  };

  const handleTemplateView = (templateId) => {
    if (templateId) {
      let selectedTemplate = originalTemplates.find(
        (template) => template?.id === selectedValue?.id,
      );
      if (selectedTemplate) {
        onViewToggleTemplate(
          true,
          true,
          originalTemplates.find((template) => template?.id === selectedValue?.id),
        );
      }
    }
  };

  const onViewDetails = (template, e) => {
    if (
      e.target.className === 'MuiTableCell-root MuiTableCell-body' ||
      e.target.className === 'MuiTableCell-root MuiTableCell-body MuiTableCell-alignCenter' ||
      e.target.className === 'MuiTableCell-root MuiTableCell-body MuiTableCell-alignRight'
    ) {
      onViewToggleTemplate(true, true, template);
    }
  };

  return (
    <div className='mt-4 mb-4 group-list-container'>
      {selectedValue.id && selectedValue.status === true && (
        <DeletePopupModel
          deletePopUp={selectedValue}
          onDelete={deleteTemplate}
          onDeleteCancel={onDeleteCancel}
        />
      )}
      <div className='row mx-2 d-flex'>
        <div className='col-12 col-lg-4'>
          {templates && (
            <TextField
              className='w-100'
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setSearchButton(e?.target?.value ? false : true);
              }}
              label='Search'
              InputProps={
                searchButton && {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }
            />
          )}
        </div>
        <div className='col-12 col-lg-8 pt-3 pt-lg-0 d-flex justify-content-end'>
          <Button
            variant='contained'
            color='primary'
            size='large'
            startIcon={<AddCircle />}
            onClick={(e) => {
              onOpenDrawer(e, GUEST_ROOM_EXPERIENCE_TEMPLATE_CREATE);
            }}
          >
            New Template
          </Button>
        </div>
      </div>
      <div className='row mx-2 d-flex mt-4'>
        {loading ? (
          <div className='pt-4 col-md-12'>
            <LinearProgressBar />
          </div>
        ) : (
          <TableContainer>
            <Table className='list-table' aria-label='simple table'>
              <TableHead>
                <TableRow>
                  <TableCell className='w-25'>Template Name</TableCell>
                  <TableCell align='center'>No.of Rooms</TableCell>
                  <TableCell align='right'>Room Areas</TableCell>
                  <TableCell align='right'>Lights</TableCell>
                  <TableCell align='right'>Scenes</TableCell>
                  <TableCell align='right'>Thermostat</TableCell>
                  <TableCell align='right'>Curtain</TableCell>
                  <TableCell align='right'></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates?.length > 0 &&
                  templates.map((template, i) => (
                    <TableRow
                      key={i}
                      className='border-white pointer'
                      onClick={(e) => {
                        onViewDetails(template, e);
                      }}
                    >
                      <TableCell>{template.name === '' ? '-No Name-' : template.name}</TableCell>
                      <TableCell align='center'>{template?.statistics?.rooms}</TableCell>
                      <TableCell align='right'>{template?.statistics?.areas}</TableCell>
                      <TableCell align='right'>{template?.statistics?.lights}</TableCell>
                      <TableCell align='right'>{template?.statistics?.scenes}</TableCell>
                      <TableCell align='right'>{template?.statistics?.thermostats}</TableCell>
                      <TableCell align='right'>{template?.statistics?.curtains}</TableCell>
                      <TableCell align='right' className='kebab-menu'>
                        <Button
                          aria-controls='simple-menu'
                          aria-haspopup='true'
                          onClick={(e) => {
                            handleCellMenuClick(e, template);
                          }}
                        >
                          <MoreHoriz />
                        </Button>
                        <Menu
                          id={i}
                          anchorEl={selectedValue?.anchorEl}
                          disableRestoreFocus
                          MenuListProps={{ autoFocusItem: false }}
                          keepMounted
                          open={Boolean(selectedValue.anchorEl) && selectedValue.id === template.id}
                          onClose={handleClose}
                          className='shadow'
                        >
                          <MenuItem
                            className='pe-4'
                            onClick={(e) => {
                              onOpenDrawer(e, GUEST_ROOM_EXPERIENCE_TEMPLATE_EDIT);
                            }}
                          >
                            <CreateIcon className='me-2 action-menu-icon' />
                            Edit
                          </MenuItem>
                          <MenuItem
                            className='pe-4'
                            onClick={(e) => {
                              deleteFlag();
                            }}
                          >
                            <DeleteForeverIcon className='me-2 action-menu-icon' />
                            Delete
                          </MenuItem>
                          <MenuItem
                            className='pe-4'
                            onClick={(e) => {
                              handleTemplateView(selectedValue?.id);
                            }}
                          >
                            <Visibility className='me-2 action-menu-icon' />
                            View
                          </MenuItem>
                          <MenuItem
                            className='pe-4'
                            onClick={(e) => {
                              duplicateTemplate();
                            }}
                          >
                            <ContentCopyIcon className='me-2 action-menu-icon' />
                            Duplicate
                          </MenuItem>
                          <MenuItem
                            className='pe-4'
                            onClick={(e) => {
                              onOpenDrawer(e, GUEST_ROOM_EXPERIENCE_TEMPLATE_ADD_ROOM);
                            }}
                          >
                            <AddCircle className='me-2 action-menu-icon' />
                            Add Rooms
                          </MenuItem>
                        </Menu>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
              <TableFooter></TableFooter>
            </Table>
            {!templates && (
              <div className='d-flex justify-content-center mt-2'>No records found</div>
            )}
          </TableContainer>
        )}
      </div>
    </div>
  );
};

export default GuestRoomExperienceTemplateList;
